import {EditableText, Icon, NumericInput, Position, Spinner, Switch} from '@blueprintjs/core'
import {ModalCatalogItemSeasonSelectors} from '@lazy'
import {utils} from '@modules'
import {AnnotateImages, BillOfMaterialsDetails, BillOfMaterialsUsageDetails, CatalogFieldRenderer, ColorPicker, DropdownCardField, Emersya3dViewer, EnumListRenderer, EnumTextRenderer, FieldRendererStore, ModalCardsField, NestedXRefCards, SidebarSpecification, SyncFieldButton, TagListChipsRenderer, XRefSidebarField} from '@modules/CatalogItemDetails'
import {CatalogItemComments} from '@lazy'
import {YenIcon} from '@stores/ui'
import {computedFn} from 'mobx-utils'
import * as css from './CatalogFieldRenderer.css'
import {CheckboxInput, MyImage} from 'centric-ui-components'
import {_, classNames, Highlighter, React, Fragment, Tooltip2, tinycolor} from '@api'
import { Tooltip } from '@material-ui/core'
import {CatalogFieldRendererEditableHtml } from './CatalogFieldRendererEditableHtml'

export const fieldRendererValue = computedFn((store: FieldRendererStore) => {
  const {
      strings, catalog, type: valueType, props: {align}, cardFieldStore, section,
      field, detailsStore: {index, adoptionStore},
      onChangeFieldValue,
      editableFieldText,
      onCancelEditableField,
      onNumericValueChange,
      onChangeEditableField,
      onBeginEditEditableField,
      onInputKeyDown,
      onInputBlur, stores,
      itemDetailsStore,
      precisionFormatter, isNumericField, isSearchResult, data, renderer, type, label, searchWords, fieldLabel,
      editable, computedPathLoading, currencyIcon, catalogField, value: {valAsString, val}, readOnly, requiredParentField, parentObject, computedValue, isComputedField, computedAdoptionCountAndBoolean, activeTabSection
    } = store,
    {_fieldByName} = catalog,
    defaultValue = catalog.defaultValueForAttribute(field.field),
    emptyValue = stores.ui?.settings?.showNoValueAsEmpty ? strings.emptyLabel : strings.noValue,
    {deployments: {isNewSync}} = stores

  if (field.agg) {
    const d = _.get(data, field?.baseNode ?? field.field)
    switch (field.agg) {
      case 'sum': {
        const sum = Array.isArray(d) ? d.reduce((acc, val) => (acc + _.get(val, field.field)), 0) : _.get(d, field.field) ?? d
        return <div key={`agg_${field.field}`}>{sum}</div>
      }
      case 'count': {
        const count = (isComputedField && adoptionStore?.adoptions) ? computedAdoptionCountAndBoolean : Array.isArray(d) ? d.reduce((acc, val) => (acc + (_.get(val, field.field) ? 1 : 0)), 0) : _.get(d, field.field) ?? d
        return <div key={`count_${field.field}`}>{count}</div>
      }
      case 'avail': {
        const isChecked = (isComputedField && adoptionStore?.adoptions) ? computedAdoptionCountAndBoolean : Array.isArray(d) ? d.some((val) => !!_.get(val, field.field)) : _.get(d, field.field) ?? d
        return <CheckboxInput className={css.checkbox}
                              checked={Boolean(isChecked)}
                              onChange={onChangeFieldValue(field, type)}
                              disabled={readOnly}
                              label={!isSearchResult ? label : <Highlighter className={css.availAgg} textToHighlight={label} searchWords={searchWords}/>}
                              name={label}/>
      }
    }
  }
  if (type == 'Enum' || type == 'LookupItem') {
    return <XRefSidebarField align={align} field={field} className={css.xrefField}/>
  }

  if (isComputedField && catalogField) {
    const comp = _.get(data, field.field)
    const {def} = catalogField
    if (computedPathLoading && computedValue === null) return <Spinner size={20}/>

    if (type !== 'Boolean') return `${currencyIcon ? `${currencyIcon}` : ''}${def?.precision ? precisionFormatter.format(computedValue) : computedValue?.toLocaleString() ?? ''}${def?.suffix ?? ''}`
  }

  if (field.fieldGroup) { // Array of secondaries is primary use case
    if (field?.baseNode) {
      let d = _.get(data, field.baseNode)
      const section = catalog.sectionById[field.baseNode]
      if (Array.isArray(d) && section?.viewOptions?.field) {
        d = _.orderBy(d, section?.viewOptions?.field)
      }

      return <div className={css.fieldGroup}>
        {Array.isArray(d) ? d?.map(secondary => <CatalogFieldRenderer
          key={secondary.exId}
          align={align}
          section={{id: secondary.exId, type: 'adoption'}}
          field={{field: _.first(field.fieldGroup), headerLabel: false, label: _.get(secondary, field.header?.field), ..._.omit(field, ['fieldGroup', 'label'])}}
        />) : []}
      </div>
    } else {
      const allFieldDefOfGroup = field.fieldGroup?.reduce((acc, f) => acc = [...acc, _fieldByName[f]].filter(_.identity), [])
      return <div className={css.fieldGroup}>
        {_.orderBy((allFieldDefOfGroup ?? []), o => o.def?.displayName?.plural).map(f => <CatalogFieldRenderer align={align} key={f.field} field={f}/>)}
      </div>
    }
  }

  if ((catalogField?.esIndex) && catalogField?.renderer === 'text') {
    return <DropdownCardField/>
  }

  switch (renderer) {
    case 'bom': {
      return <BillOfMaterialsDetails className={css.bom} config={field?.bom}/>
    }

    case 'bom-usages': {
      return <BillOfMaterialsUsageDetails className={css.bom} workspace={field.workspace}/>
    }

    case 'cards': {
      return <ModalCardsField/>
    }

    case 'comments': {
      return <CatalogItemComments field={field} data={data} esIndex={index}/>
    }

    case '3d-emersya': {
      return (<Emersya3dViewer className={css.viewer3d}/>)
    }

    case 'inline-images': {
      return <div className={css.images} data-length={val?.length ?? 0}>
        {val?.map((img, i) => {
            const url = catalog.imageUrlFor(img)

            return <img key={i} src={url} onClick={() => utils.openDataImgInNewTab(url)}/>
          }
        )}
        {!val?.length && <div className={css.empty}>
			No Images
		</div>}
      </div>
    }

    case 'EnumList':
      return <EnumListRenderer/>

    case 'inline': {
      const imageUrl = !val ? null
                            : !_.isEmpty(val) ? catalog.imageUrlFor(_.first(val)) : null

      return <div className={css.images} data-length={val?.length ?? 0}>
        {imageUrl ?
         (imageUrl?.startsWith('#')
          ? <div className={css.hex} style={{backgroundColor: imageUrl}}/>
          : <MyImage
            className={classNames(css.specImage)}
            imageUrl={imageUrl}
          />)
                  : val ? <div className={css.placeholder}><span>{'No Image'}</span></div>
                        : null}
      </div>
    }
    case 'headerCustomBoolean':
      return <CheckboxInput className={css.headerCustomFormCheckbox}
                            checked={Boolean(val)}
                            disabled={readOnly}
                            checkboxCss={css.headerCustomCheckboxCss}
                            onChange={onChangeFieldValue(field, type)} name={label}/>
    case 'headerCustomEnumType':
      return <EnumTextRenderer field={field} valAsString={valAsString}/>
    case 'headerCustomString':
      return <span className={css.rectangle}>
                        <span className={css.customString}>{`${_.isNull(valAsString) || valAsString==strings.noValue ? strings.emptyLabel : valAsString === '' ? emptyValue : valAsString}${catalogField?.def?.suffix ?? ''}`}</span>
                    </span>
    case 'Boolean':
      const checked = isComputedField ? (computedValue ?? false) : isNewSync? String(val).toLowerCase() == "true" :Boolean(val)
      if (cardFieldStore) {
        return <Switch checked={checked}
                       disabled={readOnly}
                       label={label}
                       onChange={onChangeFieldValue(field, type, section)}/>
      } else {
        return <CheckboxInput
          className={css.checkbox}
          checked={checked}
          label={!isSearchResult ? label : <Highlighter className={css.booleanCheckboxLabel} textToHighlight={label} searchWords={searchWords}/>}
          disabled={readOnly}
          onChange={onChangeFieldValue(field, type, section)}/>
      }

    case 'date':
    case 'DateTime':
      return val
             ? new Date(_.isNumber(val)
                        ? (val * 1000)
                        : Date.parse(valAsString)).toLocaleDateString()
             : emptyValue

    case 'annotate-images':
      return <AnnotateImages/>
    case 'plmHyperLink':
      const exId: string = val && val.exId || valAsString,
        title: string = (val && val.name?.trim() ? val.name : data && data.name) || emptyValue
      return <a href={utils.plmLinkFor({exId})} target='_blank'>{title}</a>
    case 'plmLink':
      return <a href={utils.plmLinkFor({exId: valAsString})} target='_blank'>{valAsString}</a>
    case 'Hex':
      let hex
      if(val){
       const colorPath = field.field.split('.'),
        colorType = _.last(colorPath)
        switch (colorType) {
          case 'rgb':
            const rgb = val.startsWith('(') ? `rgb(${val})` : val.startsWith('rgb(') ? val : val,
            color = tinycolor(rgb)
            hex = `#${color.toHex()}`
            break
          case 'hex':
            hex = val
            break  
        }
      }
      return <ColorPicker color={val} hex={hex} colorField={field} detailStore={itemDetailsStore}/> //<div style={{width: 24, height: 24, backgroundColor: val, borderRadius: 2}} />
    case 'sync':
      return <Fragment>
        <SyncFieldButton className={css.syncButton} value={val} isNewSync={isNewSync}/>
      </Fragment>

    case 'seasonSelector':
      return <ModalCatalogItemSeasonSelectors position={Position.LEFT_TOP}/>

    case 'html':
      //<Tooltip2 content={utils.removeTags(valAsString)} disableHoverListener={valAsString?.length < 60}>
      //</Tooltip2>

      if (!readOnly) {
        return <CatalogFieldRendererEditableHtml />
      }
      return valAsString ? <Fragment>
        <span className={css.html} dangerouslySetInnerHTML={{__html: valAsString}}/>
      </Fragment> : emptyValue
    case 'TagList':
      //TODO - create component to render tag list, and show chip instead of comma separated string
      // return <span>{_.isArray(val) ? val.map(v => v.name).join(', ') : '--'}</span>
      return <TagListChipsRenderer/>

    default: {
      if (catalogField?.esIndex) {
        return <SidebarSpecification popoverPosition='right'/>
      }

      if (_.isUndefined(field.field) && field.nested) {
        const nestedData = _.get(data, field?.nested?.field)
        return <NestedXRefCards nestedListItem={data} collapsible={false}/>
      }

      //   return <span className={css.notFound}>{field.field} not configured in catalog</span>
      // }

      if (editable || renderer == 'editableText') {
        const currency = catalogField?.def?.currency ?? catalogField?.def?.currency,
          placeholder = readOnly ? '' : cardFieldStore?.sectionConfig?.viewOptions?.field ? strings.emptyLabel : align == 'modal'
                                                                                                                 ? defaultValue ?? (requiredParentField && readOnly ? `Requires ${catalog.labelForField(requiredParentField)}` : `Enter ${fieldLabel}...`)
                                                                                                                 : `Enter ${fieldLabel}...`
        let editFieldTextValue = requiredParentField && !parentObject && readOnly ? '' :
                                   (editableFieldText?.field == field.field && section?.id == editableFieldText?.section?.id)
                                   ? editableFieldText?.value
                                   : (_.isNaN(val)
                                      ? 0
                                      : val ? renderer == 'text' && (val == strings.noValue || _.isNull(val)) ? strings.emptyLabel : val : catalog.defaultValueForAttribute(field.field) ??
                                                                                                                                           (type == 'Float' || type == 'Int' ? 0 : ''))

        //section?.id && console.log(editFieldTextValue, section?.id, field.field)

        if (_.isPlainObject(editFieldTextValue)){
          console.warn(`Invalid text value for field ${field}`, editFieldTextValue)
          editFieldTextValue = 'Invalid Config'
        }
        const numericInputValue = requiredParentField && !parentObject && readOnly ? '' : (editableFieldText?.field == field.field && section?.id == editableFieldText?.section?.id) ? editableFieldText?.value?.toString() : _.isNaN(val) ? '0' : val?.toString() ?? (readOnly ? '' : (catalog.defaultValueForAttribute(field.field) ?? 0))
        return <span onBlur={onCancelEditableField} className={css.editableTextContainer} data-type={isNumericField ? 'NumericInput' : 'EditableText'}>
                  {isNumericField ?
                   <NumericInput
                     data-disabled={readOnly}
                     readOnly={readOnly}
                     allowNumericCharactersOnly
                     disabled={readOnly}
                     selectAllOnFocus
                     leftIcon={!currency ? undefined : currency == 'yen' ? <YenIcon data-icon='currency' className='bp3-icon'/> : <Icon data-icon='currency' iconSize={12} icon={currency == 'usd' ? 'dollar' : currency == 'eur' ? 'euro' : 'blank'}/>}
                     placeholder={placeholder}
                     className={css.editableText}
                     value={requiredParentField && !parentObject && readOnly
                            ? ''
                            : (editableFieldText?.field == field.field && section?.id == editableFieldText?.section?.id)
                              ? editableFieldText.value.toString()
                              : precisionFormatter.format(_.isNaN(val) ? 0 : numericInputValue)
                                ?? (readOnly ? '' : (catalog.defaultValueForAttribute(field.field) ?? 0))}
                     onKeyDown={onInputKeyDown(field.field, section)}
                     onBlur={onInputBlur(field.field, section)}
                     onValueChange={onNumericValueChange(field.field, section)}
                     min={0}
                     minorStepSize={type == 'Int' ? 1 : catalogField.def?.precision ? 1 / Math.pow(10, catalogField.def.precision) : .0001}
                   /> :
                   (/*<input
	                      placeholder={placeholder}
	                      className={css.editableText}
	                      readOnly={readOnly}
                        title={editFieldTextValue ?? ''}
                        value={editFieldTextValue ?? ''}
                        onChange={store.onChangeEditableField2}
                        onKeyDown={onInputKeyDown(field.field, section)}
                        onFocus={onBeginEditEditableField(field.field, section?.id, activeTabSection, cardFieldStore)}
                        onBlur={store.onInputBlur(field.field, section)}
                      /> ||*/
                    <EditableText
                        placeholder={placeholder}
                        className={css.editableText}
                       // title={editFieldTextValue ?? ''}
                        disabled={readOnly}
                        alwaysRenderInput={!readOnly}
                        confirmOnEnterKey
                        value={editFieldTextValue ?? ''}
                        onEdit={onBeginEditEditableField}
                        onCancel={onCancelEditableField}
                        onConfirm={onChangeFieldValue({...field, valueType}, 'editableText', section)}
                        onChange={onChangeEditableField}
                        multiline={false}
                      />
                   )}
                </span>
      }
      if(renderer=='text'){
        return valAsString==strings.noValue || _.isNull(valAsString) ? strings.emptyLabel : valAsString
      }
      return`${_.isNull(valAsString) ? strings.emptyLabel : valAsString === '' ? emptyValue : currencyIcon ? `${currencyIcon}${isNumericField ? precisionFormatter.format(parseFloat(valAsString)) : valAsString?.toLocaleString()}` : isNumericField ? precisionFormatter.format(parseFloat(valAsString)) : valAsString?.toLocaleString()}${catalogField?.def?.suffix ?? ''}`
    }
  }

  return null
})


// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/FieldRenderers/CatalogFieldRendererValue.tsx